import { useCallback, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export type CrmScoringWeightKey =
  | "email_opened"
  | "email_clicked"
  | "form_submitted"
  | "page_visited"
  | "call_completed"
  | "message_engaged"
  | "webinar_registered"
  | "webinar_attended"
  | "booking_completed"
  | "deal_created"
  | "deal_won"
  | "payment_completed"
  | "meeting_booked"
  | "task_completed";

export type CrmEventActionPriority = "low" | "medium" | "high" | "urgent";
export type CrmEventActionExecutionMode = "suggest_only";

export type CrmEventActionIntent =
  | "update_lead_score"
  | "update_lifecycle_stage"
  | "update_company_profile"
  | "create_follow_up_task"
  | "update_follow_up_task"
  | "log_activity"
  | "open_sales_deal"
  | "update_sales_deal"
  | "advance_deal"
  | "tag_contact"
  | "remove_contact_tag"
  | "no_action";

export interface CrmEventActionScoreRequest {
  eventType: string;
  activityType?: string | null;
  scoreDelta?: number | null;
  personId?: string | null;
  contactId?: string | null;
  dealId?: string | null;
  companyId?: string | null;
  currentLeadScore?: number | null;
  payload?: Record<string, unknown> | null;
  weights?: Partial<Record<CrmScoringWeightKey, number>>;
}

export interface CrmActionObservedOutcome {
  windowDays: number;
  reviewed: number;
  accepted: number;
  materialized: number;
  crmMaterialized: number;
  manualResolved: number;
  acceptanceRate: number;
  materializationRate: number;
  crmMaterializationRate: number;
  manualResolutionRate: number;
  avgTimeToMaterializeMinutes: number | null;
  lastReviewedAt: string | null;
  lastAcceptedAt: string | null;
  lastMaterializedAt: string | null;
  evidenceAgeDays: number | null;
  confidence: "none" | "low" | "medium" | "high";
}

export interface CrmActionOutcomeScoring {
  baseConfidence: number;
  adjustedConfidence: number;
  outcomeLift: number;
  stalePenalty: number;
  rankScore: number;
  reason: string;
}

export interface CrmEventActionRecommendation {
  id: string;
  actionRef: string;
  intent: CrmEventActionIntent;
  priority: CrmEventActionPriority;
  confidence: number;
  reason: string;
  suggestedInput: Record<string, unknown>;
  requiresHumanReview: boolean;
  executionMode: CrmEventActionExecutionMode;
  readiness?: CrmActionReadiness;
  executionPlan?: CrmActionExecutionPlan;
  observedOutcome?: CrmActionObservedOutcome | null;
  outcomeScoring?: CrmActionOutcomeScoring;
}

export type CrmActionReadinessStatus =
  | "ready"
  | "needs_human_review"
  | "blocked_invalid_input"
  | "blocked_unknown_action";

export interface CrmActionReadinessIssue {
  code: string;
  message: string;
  path: string;
}

export interface CrmActionReadiness {
  actionRef: string;
  status: CrmActionReadinessStatus;
  canExecuteAutomatically: boolean;
  requiresHumanReview: boolean;
  aiClassification: "none" | "limited" | "high";
  issues: CrmActionReadinessIssue[];
}

export type CrmActionExecutionPlanMode = "automatic_ready" | "manual_review" | "blocked";

export interface CrmActionExecutionPlan {
  contractVersion: "stero.crm_action_execution_plan.v1";
  recommendationId: string;
  actionRef: string;
  mode: CrmActionExecutionPlanMode;
  canExecuteAutomatically: boolean;
  queueForHumanReview: boolean;
  reason: string;
  readiness: CrmActionReadiness;
  actionRequest: {
    ref: string;
    input: Record<string, unknown>;
    failurePolicy: {
      action: string;
      maxRetries?: number;
      retryDelayMs?: number;
      timeoutMs?: number;
    };
    aiClassification: "none" | "limited" | "high";
  } | null;
}

export interface CrmEventActionScore {
  contractVersion: "stero.crm_event_action_score.v1";
  eventType: string;
  activityType: string | null;
  score: {
    weightKey: CrmScoringWeightKey | null;
    delta: number;
    currentLeadScore: number | null;
    projectedLeadScore: number | null;
    explanation: string;
  };
  recommendations: CrmEventActionRecommendation[];
  safety: {
    executesActions: false;
    mutatesCrm: false;
    publicAutomationAllowed: false;
    humanReviewRequiredForHighRisk: true;
  };
}

export interface CrmEventActionRecommendationReviewRequest {
  eventId?: string | null;
  eventIds?: string[] | null;
  eventTypes?: string[] | null;
  signalFamilies?: string[] | null;
  decision?: "accepted" | "dismissed";
  minConfidence?: number | null;
  days?: number | null;
  limit?: number | null;
  includeManualReview?: boolean;
  includeBlocked?: boolean;
  dryRun?: boolean;
  sourceSurface?: string | null;
}

export interface CrmEventActionRecommendationReviewResult {
  contractVersion: "stero.crm_event_action_review_bridge.v1";
  dryRun: boolean;
  decision: "accepted" | "dismissed";
  filters: {
    days: number | null;
    limit: number;
    eventIds: string[];
    eventTypes: string[];
    signalFamilies: string[];
    minConfidence: number;
    includeManualReview: boolean;
    includeBlocked: boolean;
    sourceSurface: string;
  };
  summary: {
    sourceEventsScanned: number;
    sourceEventsScored: number;
    eventsWithoutRecommendations: number;
    recommendationsScored: number;
    candidates: number;
    recorded: number;
    failed: number;
    skippedDuplicate: number;
    skippedLowConfidence: number;
    skippedManualReview: number;
    skippedBlocked: number;
  };
  recorded: Array<CrmEventActionRecommendationReviewItem & {
    eventId: string;
  }>;
  preview: CrmEventActionRecommendationReviewItem[];
  safety: {
    executesActions: false;
    mutatesCrmEntities: false;
    recordsDecisionEvents: boolean;
    executionRequiresQueueMaterializationEndpoint: true;
  };
}

export interface CrmEventActionRecommendationReviewItem {
  sourceEventId: string;
  sourceEventType: string;
  signalFamily: string;
  recommendationId: string;
  actionRef: string;
  intent: CrmEventActionIntent;
  decision: "accepted" | "dismissed";
  priority: CrmEventActionPriority;
  reason: string;
  readinessStatus: CrmActionReadinessStatus;
  executionPlanMode: CrmActionExecutionPlanMode;
  executionPlanReason: string;
  confidence: number;
  targetEntityType: string;
  targetEntityId: string;
  queueEligibility?: "executable" | "manual_review" | "blocked";
  observedMaterializationRate?: number | null;
  observedOutcomeConfidence?: string | null;
  adjustedConfidence?: number | null;
  rankScore?: number | null;
}

export async function scoreCrmEventActionsForContact(
  contactId: string,
  input: CrmEventActionScoreRequest,
): Promise<CrmEventActionScore> {
  const response = await apiFetch<{ data: CrmEventActionScore }>(
    `/crm/intelligence/contacts/${contactId}/event-action-score`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export async function scoreCrmTenantEventActions(
  input: CrmEventActionScoreRequest,
): Promise<CrmEventActionScore> {
  const response = await apiFetch<{ data: CrmEventActionScore }>(
    "/crm/intelligence/event-action-score",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export async function reviewCrmEventActionRecommendations(
  input: CrmEventActionRecommendationReviewRequest = {},
): Promise<CrmEventActionRecommendationReviewResult> {
  const response = await apiFetch<{ data: CrmEventActionRecommendationReviewResult }>(
    "/crm/intelligence/event-action-score/recommendations/review",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export function useCrmEventActionScore(contactId: string | null) {
  const [data, setData] = useState<CrmEventActionScore | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scoreEventActions = useCallback(async (input: CrmEventActionScoreRequest) => {
    if (!contactId) {
      setData(null);
      setError("Contact required");
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await scoreCrmEventActionsForContact(contactId, input);
      setData(result);
      return result;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to score event actions");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, isLoading, error, scoreEventActions, reset };
}
